import type { AgentQuestion } from "@/backend/db/drizzle/types/jsonTypes";
import type {
  AgentRunId,
  ReportItem,
} from "@/components/analysis/agentic/db_types";
import type { PageTarget } from "@/components/analysis/agentic/pages/pageTarget";
import type { StatusToolName, ToolOutputs } from "@/utils/agentic_types";

export type ToolProgressStatus = "running" | "succeeded" | "failed";

type ToolProgressOutput<K extends StatusToolName> = K extends keyof ToolOutputs
  ? ToolOutputs[K]
  : never;

export type ToolProgressPayload<K extends StatusToolName = StatusToolName> = {
  toolName: K;
  status: ToolProgressStatus;
  text?: string;
  details?: string;
  progress?: number;
  id: string;
  timestamp: number;
} & ([ToolProgressOutput<K>] extends [never]
  ? { output?: never }
  : { output?: ToolProgressOutput<K> });

export type ToolProgressMessage = {
  type: "tool-progress";
} & ToolProgressPayload;

export type LegacyToolProgressPayload = {
  text: string;
  details?: string;
  timestamp: number;
  id: string;
  debug?: boolean;
  done?: boolean;
  progress?: number;
  toolName?: StatusToolName;
  status?: ToolProgressStatus;
};

export type PersistedToolProgressPayload =
  | ToolProgressPayload
  | LegacyToolProgressPayload;

export type DebugMessage = {
  type: "debug";
  text: string;
  id: string;
  timestamp: number;
};

// Web link item with byte positions for citation placement
export type PositionedWebLink = {
  url: string;
  title: string;
  startIndex: number; // UTF-8 byte offset of segment start in raw response text
  endIndex: number; // UTF-8 byte offset of segment end (exclusive) in raw response text
};

export type AnalysisChatEvent =
  | ToolProgressMessage
  | DebugMessage
  | {
      type: "init";
      agentRunId: AgentRunId | null;
      langsmithTraceId?: string;
    }
  | {
      type: "iteration-start";
      iterationNumber: number;
    }
  | {
      type: "report-item";
      item: ReportItem;
    }
  | {
      type: "report-complete";
      items: ReportItem[];
    }
  | {
      type: "error";
      error: string;
    }
  | {
      type: "finish";
    }
  | {
      type: "invalidate-queries";
      queryKeys: unknown[][];
    }
  | { type: "invalidate-feature-table" }
  | { type: "navigate"; target: PageTarget }
  | {
      type: "agent-questions";
      /** Replaces the questions on the turn's assistant message, so a replayed event is harmless. */
      questions: AgentQuestion[];
    };

export type AnalysisChatEventEnvelope = {
  eventIndex: number;
  event: AnalysisChatEvent;
};

// The chat route answers a reconnect with this status once the turn's run has closed.
export const ANALYSIS_CHAT_TURN_ENDED_STATUS = 410;

export type AnalysisChatClientCommand = {
  type: "replay";
  fromIndex: number;
};

export type AgentStreamEvent =
  | ToolProgressMessage
  | DebugMessage
  | { type: "init"; agentRunId: AgentRunId }
  | { type: "iteration-start"; iterationNumber: number }
  | { type: "text-delta"; delta: string }
  | {
      type: "text-complete";
      fullText: string;
      webLinkItems: PositionedWebLink[];
    }
  | { type: "error"; error: string }
  | { type: "finish"; agentRunId: AgentRunId }
  | { type: "invalidate-queries"; queryKeys: unknown[][] }
  | { type: "invalidate-feature-table" }
  | { type: "navigate"; target: PageTarget };
